/**
 * fetchConstituenciesBaseline.js
 * ---------------------------------------------------------------
 * Fetches one row per constituency for the active group/filters.
 * Used as the national baseline for sidebar aggregates and
 * hover-tooltip benchmarks.
 *
 * Extracted from MapPage.fetchConstituenciesBaseline.
 *
 * @param {function} params.getActiveGroup
 * @param {function} params.getFilters
 * @param {object}   params.props
 * @param {function} params.setState
 * @param {object}   params._tracking  - { inflight, lastKey, lastAt } mutated in place
 */
import { tableFromIPC } from "apache-arrow";
import { rowsFromTable } from "./helpers";
import { schemaRequestFields } from "../schemaTypes";

export async function fetchConstituenciesBaseline({ getActiveGroup, getFilters, props, setState, _tracking }) {
  const group = getActiveGroup();
  const isExplore = props.ActionMode === "explore";
  const targetSubVar1 = props?.ActionTargetSubVariables?.TargetSubVariable1;
  const resolveFilterKeys = (targetVar) => {
    if (!targetVar) return [];
    if (targetVar.type !== "likert_grid") return targetVar.keys || [];
    const subVarIds = targetVar.subVariables || [];
    const allKeys = subVarIds.flatMap((svId) => props.groupsById?.[svId]?.keys || []);
    return Array.from(new Set(allKeys));
  };
  const baselineKeys = isExplore
    ? (group?.keys || [])
    : [
        ...resolveFilterKeys(group?.TargetVariable1),
        ...(group?.TargetVariable2?.keys || []),
      ];
  const baselineSchema = isExplore
    ? group?.schema
    : (group?.TargetVariable1?.schema || group?.TargetVariable2?.schema || group?.schema);

  if (!group || !baselineKeys.length) {
    setState({ metricRowsConstituencies: null });
    return;
  }

  const theme = props.selectedSection || "pol";
  const filters = getFilters();
  const key = `constituency|*|${theme}|${baselineKeys.join(",")}|${targetSubVar1 || ""}|${JSON.stringify(filters)}`;
  const now = Date.now();

  if (_tracking.lastKey === key && now - _tracking.lastAt < 700) return;
  _tracking.lastKey = key;
  _tracking.lastAt = now;

  if (_tracking.inflight) _tracking.inflight.abort();
  _tracking.inflight = new AbortController();

  try {
    const res = await fetch("/api/constituency_data_arrow/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        areaCodes: [],   // empty = all constituencies
        theme,
        groupKeys: baselineKeys,
        filters,
        ...schemaRequestFields(baselineSchema),
        normalise_over_options: group?.normalise_over_options || false,
      }),
      signal: _tracking.inflight.signal,
    });
    if (!res.ok) throw new Error(`baseline ${res.status}`);
    const rows = rowsFromTable(tableFromIPC(await res.arrayBuffer()));
    setState({ metricRowsConstituencies: rows });
  } catch (e) {
    if (e.name !== "AbortError") console.error("baseline error:", e);
    setState({ metricRowsConstituencies: null });
  } finally {
    _tracking.inflight = null;
  }
}
